import type {
  BillingUsage,
  BillingSubscription,
  SubscriptionPlan,
  ContentItem,
  ContentMedia,
  ContentOutline,
  ContentPlan,
  ContentPillar,
  HumanTask,
  HumanTaskStatus,
  HumanTasksResponse,
  AccountSnapshotResponse,
  JobStatus,
  PaginatedResponse,
  ScheduledTask,
  Strategy,
  SupervisorStatus,
  InteractionStatus,
  InteractionEventsResponse,
  InteractionEventLog,
  User,
  Workspace,
} from './types';

const normalizeApiBase = (value?: string): string => {
  const base = (value || 'http://localhost:8000/api').replace(/\/+$/, '');
  return base.endsWith('/api') ? base : `${base}/api`;
};

const API_BASE = normalizeApiBase(process.env.REACT_APP_API_URL);

type RequestOptions = RequestInit & {
  headers?: HeadersInit;
};

type AuthResponse = {
  user: User;
  workspace?: Workspace;
};

type AgentJobResponse = {
  job_id: string;
  status: string;
  message?: string;
  seconds?: number;
  allowed_seconds?: number[];
};

const getCookie = (name: string): string | undefined =>
  document.cookie
    .split('; ')
    .find((entry) => entry.startsWith(`${name}=`))
    ?.split('=')[1];

const isUnsafeMethod = (method: string): boolean =>
  !['GET', 'HEAD', 'OPTIONS', 'TRACE'].includes(method.toUpperCase());

const ensureCsrfCookie = async (): Promise<void> => {
  if (getCookie('csrftoken')) {
    return;
  }

  await fetch(`${API_BASE}/accounts/csrf/`, {
    method: 'GET',
    credentials: 'include',
  });
};

async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const url = `${API_BASE}${path}`;
  const method = (options.method || 'GET').toUpperCase();

  if (isUnsafeMethod(method)) {
    await ensureCsrfCookie();
  }

  const csrfToken = getCookie('csrftoken');
  const headers = new Headers(options.headers);
  headers.set('Content-Type', 'application/json');
  if (csrfToken && isUnsafeMethod(method)) {
    headers.set('X-CSRFToken', csrfToken);
  }

  const config: RequestOptions = {
    ...options,
    headers,
    credentials: 'include',
  };

  const response = await fetch(url, config);
  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Request failed' }));

    const firstFieldError = (() => {
      if (!error || typeof error !== 'object' || Array.isArray(error)) return null;
      for (const value of Object.values(error as Record<string, unknown>)) {
        if (Array.isArray(value) && value.length > 0) {
          const first = value[0];
          if (typeof first === 'string') return first;
        }
      }
      return null;
    })();

    // Handle quota exceeded errors specially
    if (response.status === 402 || (response.status === 409 && error.detail === 'quota_exceeded')) {
      const quotaError: any = new Error(error.message || error.detail || 'Quota exceeded');
      quotaError.isQuotaExceeded = true;
      quotaError.upgrade = error.upgrade;
      quotaError.usageSnapshot = error.usage_snapshot;
      quotaError.detail = error.detail;
      throw quotaError;
    }

    // A generation run is already in flight for this post. Surface the flag and
    // the existing job id so callers can offer an "override & restart" path.
    if (response.status === 409 && error.already_running) {
      const conflict: any = new Error(error.error || 'Already generating for this post.');
      conflict.alreadyRunning = true;
      conflict.jobId = error.job_id ?? null;
      throw conflict;
    }

    throw new Error(firstFieldError || error.detail || error.error || 'Request failed');
  }

  if (response.status === 204) {
    return {} as T;
  }

  const contentLength = response.headers.get('content-length');
  if (contentLength === '0') {
    return {} as T;
  }

  const text = await response.text();
  if (!text) {
    return {} as T;
  }

  return JSON.parse(text) as T;
}

// Authentication
export const register = (data: { username: string; email?: string; password: string }) =>
  request<AuthResponse>('/accounts/register/', { method: 'POST', body: JSON.stringify(data) });
export const login = (data: { email?: string; username?: string; password: string }) =>
  request<AuthResponse>('/accounts/login/', { method: 'POST', body: JSON.stringify(data) });
export const logout = () =>
  request<Record<string, unknown>>('/accounts/logout/', {
    method: 'POST',
    body: JSON.stringify({}),
  });
export const getCurrentUser = () => request<{ user: User }>('/accounts/me/');
export const updateCurrentUser = (data: {
  username?: string;
  email?: string;
  country_code?: string;
}) => request<{ user: User }>('/accounts/me/', { method: 'PATCH', body: JSON.stringify(data) });

// Workspace
export const getWorkspaces = () => request<PaginatedResponse<Workspace>>('/accounts/workspaces/');
export const getWorkspace = (id: number) => request<Workspace>(`/accounts/workspaces/${id}/`);
export const updateWorkspace = (
  id: number,
  data: Partial<Workspace> & { instagram_access_token?: string }
) =>
  request<Workspace>(`/accounts/workspaces/${id}/`, {
    method: 'PATCH',
    body: JSON.stringify(data),
  });

// Strategy
export const getStrategies = () => request<PaginatedResponse<Strategy>>('/content/strategies/');
export const createStrategy = (workspaceId: number, data: Partial<Strategy>) => {
  // Remove workspace from data as it's read-only and will be injected server-side
  const { workspace: _w, ...bodyData } = data;
  void _w;
  return request<Strategy>(`/content/strategies/?workspace_id=${workspaceId}`, {
    method: 'POST',
    body: JSON.stringify(bodyData),
  });
};
export const updateStrategy = (id: number, data: Partial<Strategy>) =>
  request<Strategy>(`/content/strategies/${id}/`, { method: 'PATCH', body: JSON.stringify(data) });

// Content Pillars
export const getPillars = () => request<PaginatedResponse<ContentPillar>>('/content/pillars/');
export const createPillar = (workspaceId: number, data: Partial<ContentPillar>) => {
  const { workspace: _w, ...bodyData } = data;
  void _w;
  return request<ContentPillar>(`/content/pillars/?workspace_id=${workspaceId}`, {
    method: 'POST',
    body: JSON.stringify(bodyData),
  });
};
export const updatePillar = (id: number, data: Partial<ContentPillar>) =>
  request<ContentPillar>(`/content/pillars/${id}/`, {
    method: 'PATCH',
    body: JSON.stringify(data),
  });
export const deletePillar = (id: number) =>
  request<Record<string, unknown>>(`/content/pillars/${id}/`, { method: 'DELETE' });

// Content Plans
export const getPlans = () => request<PaginatedResponse<ContentPlan>>('/content/plans/');
export const createPlan = (workspaceId: number, data: Partial<ContentPlan>) => {
  const { workspace: _w, ...bodyData } = data;
  void _w;
  return request<ContentPlan>(`/content/plans/?workspace_id=${workspaceId}`, {
    method: 'POST',
    body: JSON.stringify(bodyData),
  });
};
export const updatePlan = (id: number, data: Partial<ContentPlan>) =>
  request<ContentPlan>(`/content/plans/${id}/`, { method: 'PATCH', body: JSON.stringify(data) });
export const deletePlan = (id: number) =>
  request<Record<string, unknown>>(`/content/plans/${id}/`, { method: 'DELETE' });

// Content Outlines
export const getOutlines = (params?: {
  page?: number;
  page_size?: number;
  ordering?: string;
  status?: string;
  archived?: boolean | 'all';
}) => {
  const queryParams = new URLSearchParams();
  if (params?.page) queryParams.append('page', String(params.page));
  if (params?.page_size) queryParams.append('page_size', String(params.page_size));
  if (params?.ordering) queryParams.append('ordering', params.ordering);
  if (params?.status && params.status !== 'all') queryParams.append('status', params.status);
  if (params?.archived === true) queryParams.append('archived', 'true');
  if (params?.archived === false) queryParams.append('archived', 'false');
  if (params?.archived === 'all') queryParams.append('archived', 'all');
  const queryString = queryParams.toString();
  const path = `/content/outlines/${queryString ? `?${queryString}` : ''}`;
  return request<PaginatedResponse<ContentOutline>>(path);
};
export const getOutline = (id: number) => request<ContentOutline>(`/content/outlines/${id}/`);
export const createOutline = (workspaceId: number, data: Partial<ContentOutline>) => {
  const { workspace: _w, ...bodyData } = data;
  void _w;
  return request<ContentOutline>(`/content/outlines/?workspace_id=${workspaceId}`, {
    method: 'POST',
    body: JSON.stringify(bodyData),
  });
};
export const updateOutline = (id: number, data: Partial<ContentOutline>) =>
  request<ContentOutline>(`/content/outlines/${id}/`, {
    method: 'PATCH',
    body: JSON.stringify(data),
  });
export const deleteOutline = (id: number) =>
  request<Record<string, unknown>>(`/content/outlines/${id}/`, { method: 'DELETE' });
export const archiveOutline = (id: number) =>
  request<ContentOutline>(`/content/outlines/${id}/archive/`, { method: 'POST' });
export const unarchiveOutline = (id: number) =>
  request<ContentOutline>(`/content/outlines/${id}/unarchive/`, { method: 'POST' });

// Content Items
export const getContentItems = () => request<PaginatedResponse<ContentItem>>('/content/items/');
// Fetch the single content item for an outline. The list endpoint only returns
// the newest page, so this is used to load older items (caption/media) on demand
// when they aren't present in the cached page.
export const getContentItemByOutline = async (
  outlineId: number
): Promise<ContentItem | null> => {
  const res = await request<PaginatedResponse<ContentItem>>(
    `/content/items/?outline=${outlineId}`
  );
  return res.results[0] ?? null;
};
export const updateContentItem = (id: number, data: Partial<ContentItem>) =>
  request<ContentItem>(`/content/items/${id}/`, { method: 'PATCH', body: JSON.stringify(data) });
export const publishContentItem = (id: number) =>
  request<{
    status: string;
    published_via: 'system';
    instagram_post_id: string;
    published_at: string;
  }>(`/content/items/${id}/publish/`, { method: 'POST' });
export const markContentItemManuallyUploaded = (id: number, instagramPostId?: string) =>
  request<{
    status: string;
    published_via: 'manual';
    instagram_post_id: string;
    published_at: string;
  }>(`/content/items/${id}/mark-manually-uploaded/`, {
    method: 'POST',
    body: JSON.stringify(instagramPostId ? { instagram_post_id: instagramPostId } : {}),
  });

// Downloads a post's media + caption.txt as a single ZIP for manual upload
// (e.g. when the Instagram connection is down). Returns the blob plus the
// server-provided filename so the caller can trigger a browser download.
export const downloadContentItemMedia = async (
  id: number
): Promise<{ blob: Blob; filename: string }> => {
  const response = await fetch(`${API_BASE}/content/items/${id}/download/`, {
    method: 'GET',
    credentials: 'include',
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Download failed' }));
    throw new Error(error.detail || error.error || 'Download failed');
  }

  const disposition = response.headers.get('content-disposition') || '';
  const match = disposition.match(/filename="?([^"]+)"?/i);
  const filename = match?.[1] || `post-${id}.zip`;

  return { blob: await response.blob(), filename };
};

export const refreshInstagramPostAnalytics = (workspaceId: number, instagramPostId: string) =>
  request<{
    instagram_post_id: string;
    likes: number;
    comments: number;
    updated_content_items: number;
    refreshed_at: string;
  }>(
    `/accounts/workspaces/${workspaceId}/instagram/analytics/posts/${encodeURIComponent(
      instagramPostId
    )}/refresh/`,
    {
      method: 'POST',
    }
  );

// Content Media

const apiPathFromUrl = (url: string): string => {
  if (url.startsWith(API_BASE)) {
    return url.slice(API_BASE.length) || '/';
  }
  if (/^https?:\/\//.test(url)) {
    const parsed = new URL(url);
    const apiPrefix = '/api';
    const path = parsed.pathname.startsWith(apiPrefix)
      ? parsed.pathname.slice(apiPrefix.length) || '/'
      : parsed.pathname;
    return `${path}${parsed.search}`;
  }
  return url;
};

export const listAllMedia = async (workspaceId: number): Promise<PaginatedResponse<ContentMedia>> => {
  let path = `/content/media/?workspace_id=${workspaceId}&page_size=500`;
  const results: ContentMedia[] = [];
  let count: number | undefined;

  while (path) {
    const page = await request<PaginatedResponse<ContentMedia>>(path);
    count = page.count;
    results.push(...(page.results ?? []));
    path = page.next ? apiPathFromUrl(page.next) : '';
  }

  return {
    count,
    next: null,
    previous: null,
    results,
  };
};

export const createMedia = (
  contentItemId: number,
  data: { prompt?: string; media_type?: string; order?: number }
) =>
  request<ContentMedia>(`/content/media/?content_item_id=${contentItemId}`, {
    method: 'POST',
    body: JSON.stringify({ content_item: contentItemId, ...data }),
  });

export const updateMedia = (
  id: number,
  data: Partial<Pick<ContentMedia, 'prompt' | 'order' | 'is_hidden'>>
) =>
  request<ContentMedia>(`/content/media/${id}/`, { method: 'PATCH', body: JSON.stringify(data) });

export const deleteMedia = (id: number) =>
  request<Record<string, unknown>>(`/content/media/${id}/`, { method: 'DELETE' });

export const reorderMedia = (orderedIds: number[]) =>
  request<{ reordered: number }>(`/content/media/reorder/`, {
    method: 'POST',
    body: JSON.stringify({ ordered_ids: orderedIds }),
  });

export const uploadMediaFile = async (
  contentItemId: number,
  file: File,
  options?: { prompt?: string; mediaId?: number }
): Promise<ContentMedia> => {
  const url = `${API_BASE}/content/media/upload/`;
  await ensureCsrfCookie();
  const csrfToken = getCookie('csrftoken');

  const formData = new FormData();
  formData.append('content_item', String(contentItemId));
  formData.append('image', file);
  if (options?.prompt) formData.append('prompt', options.prompt);
  if (options?.mediaId !== undefined) formData.append('media_id', String(options.mediaId));

  const headers: HeadersInit = {};
  if (csrfToken) headers['X-CSRFToken'] = csrfToken;

  const response = await fetch(url, {
    method: 'POST',
    credentials: 'include',
    headers,
    body: formData,
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Upload failed' }));
    throw new Error(error.detail || 'Upload failed');
  }

  return response.json() as Promise<ContentMedia>;
};

// ── Content work queue API (companion issue #108) ────────────────────────────
// TODO: swap ContentPage client-side heuristics for this endpoint once live.
export interface WorkQueueRow {
  id: number;
  title: string;
  summary?: string;
  content_format: string;
  created_at?: string;
  publish_at?: string | null;
  pillar?: number | null;
  plan?: number | null;
  content_item_id?: number | null;
  content_item_status?: string | null;
  has_media: boolean;
  missing_media_count: number;
  scheduled_for?: string | null;
  published_at?: string | null;
  workflow_status:
    | 'needs_approval'
    | 'needs_content'
    | 'needs_media'
    | 'ready_to_schedule'
    | 'scheduled'
    | 'published'
    | 'in_progress'
    | 'failed';
  recommended_primary_action?: string;
}

export const getWorkQueue = (params?: {
  workspace_id?: number;
  workflow_status?: string;
  plan?: number;
  pillar?: number;
  page?: number;
  page_size?: number;
  ordering?: string;
}) => {
  const q = new URLSearchParams();
  if (params?.workspace_id) q.append('workspace_id', String(params.workspace_id));
  if (params?.workflow_status) q.append('workflow_status', params.workflow_status);
  if (params?.plan) q.append('plan', String(params.plan));
  if (params?.pillar) q.append('pillar', String(params.pillar));
  if (params?.page) q.append('page', String(params.page));
  if (params?.page_size) q.append('page_size', String(params.page_size));
  if (params?.ordering) q.append('ordering', params.ordering);
  const qs = q.toString();
  return request<PaginatedResponse<WorkQueueRow>>(`/content/workqueue/${qs ? `?${qs}` : ''}`);
};

// AI Agent - Deterministic tasks
// Note: workspace_id must be passed via query parameter, not in request body
export const generateOutlines = (workspaceId: number, data: Record<string, unknown>) => {
  const { workspace_id, ...bodyData } = data;
  const wsId = workspaceId || workspace_id;

  return request<AgentJobResponse>(`/agent/outlines/create/?workspace_id=${wsId}`, {
    method: 'POST',
    body: JSON.stringify(bodyData),
  });
};

export const generateContentItem = (workspaceId: number, data: Record<string, unknown>) => {
  const { workspace_id, ...bodyData } = data;
  const wsId = workspaceId || workspace_id;

  return request<AgentJobResponse>(`/agent/content/create/?workspace_id=${wsId}`, {
    method: 'POST',
    body: JSON.stringify(bodyData),
  });
};

export const regenerateContentItem = (workspaceId: number, data: Record<string, unknown>) => {
  const { workspace_id, ...bodyData } = data;
  const wsId = workspaceId || workspace_id;

  return request<AgentJobResponse>(`/agent/content/regenerate/?workspace_id=${wsId}`, {
    method: 'POST',
    body: JSON.stringify(bodyData),
  });
};

export const generateImages = (workspaceId: number, data: Record<string, unknown>) => {
  const { workspace_id, ...bodyData } = data;
  const wsId = workspaceId || workspace_id;

  return request<AgentJobResponse>(`/agent/images/generate/?workspace_id=${wsId}`, {
    method: 'POST',
    body: JSON.stringify(bodyData),
  });
};

export const generateCTAImage = (workspaceId: number, prompt: string) =>
  request<AgentJobResponse>(`/agent/cta-image/generate/?workspace_id=${workspaceId}`, {
    method: 'POST',
    body: JSON.stringify({ prompt }),
  });

export const uploadCTAImage = async (
  workspaceId: number,
  file: File
): Promise<{ cta_image_url: string }> => {
  const url = `${API_BASE}/accounts/workspaces/${workspaceId}/upload_cta_image/`;
  await ensureCsrfCookie();
  const csrfToken = getCookie('csrftoken');

  const formData = new FormData();
  formData.append('image', file);

  const headers: HeadersInit = {};
  if (csrfToken) headers['X-CSRFToken'] = csrfToken;

  const response = await fetch(url, {
    method: 'POST',
    credentials: 'include',
    headers,
    body: formData,
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Upload failed' }));
    throw new Error(error.detail || 'Upload failed');
  }
  return response.json() as Promise<{ cta_image_url: string }>;
};

export const deleteCTAImage = (workspaceId: number) =>
  request<{ deleted: boolean }>(`/accounts/workspaces/${workspaceId}/delete_cta_image/`, {
    method: 'POST',
    body: JSON.stringify({}),
  });

export const uploadBrandIcon = async (
  workspaceId: number,
  file: File
): Promise<{ brand_icon_url: string }> => {
  const url = `${API_BASE}/accounts/workspaces/${workspaceId}/upload_brand_icon/`;
  await ensureCsrfCookie();
  const csrfToken = getCookie('csrftoken');

  const formData = new FormData();
  formData.append('image', file);

  const headers: HeadersInit = {};
  if (csrfToken) headers['X-CSRFToken'] = csrfToken;

  const response = await fetch(url, {
    method: 'POST',
    credentials: 'include',
    headers,
    body: formData,
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Upload failed' }));
    throw new Error(error.detail || 'Upload failed');
  }
  return response.json() as Promise<{ brand_icon_url: string }>;
};

export const deleteBrandIcon = (workspaceId: number) =>
  request<{ deleted: boolean }>(`/accounts/workspaces/${workspaceId}/delete_brand_icon/`, {
    method: 'POST',
    body: JSON.stringify({}),
  });

export const generateBrandIcon = (workspaceId: number, prompt: string) =>
  request<AgentJobResponse>(`/agent/brand-icon/generate/?workspace_id=${workspaceId}`, {
    method: 'POST',
    body: JSON.stringify({ prompt }),
  });

export const generateProfileImage = (workspaceId: number, prompt: string) =>
  request<AgentJobResponse>(`/agent/profile-image/generate/?workspace_id=${workspaceId}`, {
    method: 'POST',
    body: JSON.stringify({ prompt }),
  });

export const uploadProfileImage = async (
  workspaceId: number,
  file: File
): Promise<{ profile_image_url: string }> => {
  const url = `${API_BASE}/accounts/workspaces/${workspaceId}/upload_profile_image/`;
  await ensureCsrfCookie();
  const csrfToken = getCookie('csrftoken');

  const formData = new FormData();
  formData.append('image', file);

  const headers: HeadersInit = {};
  if (csrfToken) headers['X-CSRFToken'] = csrfToken;

  const response = await fetch(url, {
    method: 'POST',
    credentials: 'include',
    headers,
    body: formData,
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Upload failed' }));
    throw new Error(error.detail || 'Upload failed');
  }
  return response.json() as Promise<{ profile_image_url: string }>;
};

export const deleteProfileImage = (workspaceId: number) =>
  request<{ deleted: boolean }>(`/accounts/workspaces/${workspaceId}/delete_profile_image/`, {
    method: 'POST',
    body: JSON.stringify({}),
  });

export const setInstagramProfilePicture = (workspaceId: number) =>
  request<{ updated: boolean }>(
    `/accounts/workspaces/${workspaceId}/set_instagram_profile_picture/`,
    {
      method: 'POST',
      body: JSON.stringify({}),
    }
  );

// Push the stored display name + bio to the live Instagram profile
export const pushInstagramIdentity = (workspaceId: number) =>
  request<{ updated: boolean; fields: string[] }>(
    `/accounts/workspaces/${workspaceId}/push_instagram_identity/`,
    {
      method: 'POST',
      body: JSON.stringify({}),
    }
  );

export const generateVideos = (workspaceId: number, data: Record<string, unknown>) => {
  const { workspace_id, ...bodyData } = data;
  const wsId = workspaceId || workspace_id;

  return request<AgentJobResponse>(`/agent/videos/generate/?workspace_id=${wsId}`, {
    method: 'POST',
    body: JSON.stringify(bodyData),
  });
};

// AI Agent - Reel Studio (theme -> images -> assembled reel)
export type ReelGenerationInput = {
  theme: string;
  count?: number;
  duration_seconds?: number;
  overlay?: boolean;
  tagline?: string;
  wordmark?: string;
};
export type ReelJobResponse = AgentJobResponse & {
  content_item_id?: string;
  outline_id?: string;
};
export const generateReel = (workspaceId: number, data: ReelGenerationInput) =>
  request<ReelJobResponse>(`/agent/reel/generate/?workspace_id=${workspaceId}`, {
    method: 'POST',
    body: JSON.stringify(data),
  });

// AI Agent - Job status
export const getJobStatus = (jobId: string) => request<JobStatus>(`/agent/job/${jobId}/`);

// AI Agent - Live activity trail for a running job
export interface JobEvent {
  event_type: string;
  data: {
    tool?: string;
    label?: string;
    call_id?: string;
    step?: number;
    agent?: string;
    ms?: number | null;
  };
  created_at: string;
}
export const getJobEvents = (jobId: string) =>
  request<{ job_id: string; job_status: string; events: JobEvent[] }>(
    `/agent/job/${jobId}/events/`
  );

// AI Agent - Active jobs
export interface ActiveJob {
  job_id: string;
  action: string;
  status: 'queued' | 'running';
  created_at: string;
  updated_at: string;
  related_object_type: string | null;
  related_object_id: string | null;
}
export const getActiveJobs = (workspaceId: number) =>
  request<{ jobs: ActiveJob[] }>(`/agent/jobs/active/?workspace_id=${workspaceId}`);

// AI Agent - Job history
export interface JobHistoryItem {
  job_id: string;
  action: string;
  status: 'completed' | 'failed';
  /** Error message for failed jobs. The sweeper prefixes lost-job errors with
   *  "Job interrupted" — the UI uses that to render a retry hint instead of a
   *  generic failure. */
  last_error: string | null;
  related_object_type: string | null;
  related_object_id: string | null;
  created_at: string;
  updated_at: string;
  completed_at: string | null;
}
export const getJobHistory = (workspaceId: number, limit = 50) =>
  request<{ jobs: JobHistoryItem[] }>(
    `/agent/jobs/history/?workspace_id=${workspaceId}&limit=${limit}`
  );

// Scheduler
export const getSchedulerTasks = (workspaceId: number) =>
  request<PaginatedResponse<ScheduledTask>>(
    `/scheduler/tasks/?workspace_id=${workspaceId}&page_size=1000`
  );
export const getSchedulerTasksForOutline = (workspaceId: number, outlineId: number) =>
  request<PaginatedResponse<ScheduledTask>>(
    `/scheduler/tasks/?workspace_id=${workspaceId}&outline_id=${outlineId}`
  );
export const deleteSchedulerTask = (id: number) =>
  request<Record<string, unknown>>(`/scheduler/tasks/${id}/`, { method: 'DELETE' });
export const executeTaskNow = (id: number) =>
  request<{ message: string; status: string }>(`/scheduler/tasks/${id}/execute_now/`, {
    method: 'POST',
  });
export const toggleScheduler = (workspaceId: number, enabled: boolean, onDisable?: string) =>
  request<{ scheduler_enabled: boolean }>(`/scheduler/workspaces/${workspaceId}/toggle/`, {
    method: 'POST',
    body: JSON.stringify({ enabled, on_disable: onDisable || 'keep' }),
  });
export const reconcileSchedulerTasks = (workspaceId: number) =>
  request<{
    message: string;
    reconciled: number;
    checked: number;
    reconciled_create_item: number;
    reconciled_create_media: number;
    reconciled_publish: number;
  }>(`/scheduler/workspaces/${workspaceId}/reconcile/`, {
    method: 'POST',
  });
export const getAccountSnapshot = (workspaceId: number) =>
  request<AccountSnapshotResponse>(`/scheduler/workspaces/${workspaceId}/snapshot/`);

// AI Agent - Session-based chat (multi-turn conversations)
export const startAgentSession = (workspaceId: number, kind: 'ONBOARDING' | 'BRAIN') =>
  request<{ session_id: string }>(`/agent/session/start/?workspace_id=${workspaceId}`, {
    method: 'POST',
    body: JSON.stringify({ kind }),
  });

export const sendSessionMessage = (workspaceId: number, sessionId: string, message: string) =>
  request<{ job_id: string }>(`/agent/session/${sessionId}/message/?workspace_id=${workspaceId}`, {
    method: 'POST',
    body: JSON.stringify({ message }),
  });

export const getSessionHistory = (workspaceId: number, sessionId: string) =>
  request<{ messages: Array<{ role: 'user' | 'assistant'; message: string }> }>(
    `/agent/session/${sessionId}/history/?workspace_id=${workspaceId}`
  );

export const listSessions = (workspaceId: number, kind: 'ONBOARDING' | 'BRAIN' = 'BRAIN') =>
  request<{
    sessions: Array<{
      session_id: string;
      kind: string;
      created_at: string;
      is_active: boolean;
    }>;
  }>(`/agent/sessions/?workspace_id=${workspaceId}&kind=${kind}`);


// Billing & Usage
export const getBillingUsage = (workspaceId: number) =>
  request<BillingUsage>(`/billing/usage/?workspace_id=${workspaceId}`);

export const getBillingPlans = () => request<SubscriptionPlan[]>('/billing/plans/');

export const getBillingSubscription = (workspaceId: number) =>
  request<BillingSubscription>(`/billing/subscription/?workspace_id=${workspaceId}`);

export const createCheckoutSession = (data: {
  workspace_id: number;
  plan_code: string;
  success_url: string;
  cancel_url: string;
}) =>
  request<{ session_id: string; url: string }>('/billing/checkout/', {
    method: 'POST',
    body: JSON.stringify(data),
  });

// Dev-only: activate a workspace without Stripe checkout. The backend gates
// this on settings.DEBUG, so it is a no-op (403) against a production API.
export const devActivateSubscription = (data: { workspace_id: number; plan_code?: string }) =>
  request<{ activated: boolean; plan_code: string; status: string }>('/billing/dev/activate/', {
    method: 'POST',
    body: JSON.stringify(data),
  });

export const reconcileCheckout = (data: { checkout_session_id?: string; workspace_id?: number }) =>
  request<{
    action: string;
    workspace_id: string;
    plan_code: string;
    status: string;
    stripe_customer_id: string;
    stripe_subscription_id: string;
    stripe_checkout_session_id: string;
  }>('/billing/reconcile-checkout/', {
    method: 'POST',
    body: JSON.stringify(data),
  });

export const createBillingPortal = (data: { workspace_id: number; return_url: string }) =>
  request<{ url: string }>('/billing/portal/', {
    method: 'POST',
    body: JSON.stringify(data),
  });

// Instagrapi – test login (password used only for this request, never stored)
export const testInstagrapiConnection = (
  workspaceId: number,
  username: string,
  password: string,
  countryCode?: string
) =>
  request<{
    valid: boolean;
    username?: string;
    session_data?: string;
    error?: string;
    auth_status?: string;
    proxy_used?: boolean;
    // Correlation id for the server-side connection log. Echo it back on save so
    // the whole attempt (login → code → save) reads as one journey in support.
    flow_id?: string;
    // challenge_pending fields
    challenge_token?: string;
    challenge_type?: string;
    masked_contact?: string;
  }>(`/accounts/workspaces/${workspaceId}/test_instagrapi/`, {
    method: 'POST',
    body: JSON.stringify({ username, password, instagram_account_country: countryCode }),
  });

// Instagrapi – save a verified session to the workspace
export const saveInstagrapiSession = (
  workspaceId: number,
  username: string,
  sessionData: string,
  countryCode?: string,
  flowId?: string
) =>
  request<{ saved: boolean; proxy_bucket_key?: string; country_code?: string }>(
    `/accounts/workspaces/${workspaceId}/save_instagrapi_session/`,
    {
      method: 'POST',
      body: JSON.stringify({
        username,
        session_data: sessionData,
        country_code: countryCode,
        flow_id: flowId,
      }),
    }
  );

// Instagrapi – submit 6-digit challenge code after Instagram sends it via email/SMS
export const submitInstagrapiChallenge = (
  workspaceId: number,
  challengeToken: string,
  code: string
) =>
  request<{
    valid: boolean;
    username?: string;
    session_data?: string;
    auth_status?: string;
    error?: string;
    proxy_used?: boolean;
    proxy_label?: string;
    proxy_bucket_key?: string;
    login_method?: string;
    flow_id?: string;
  }>(`/accounts/workspaces/${workspaceId}/submit_instagrapi_challenge/`, {
    method: 'POST',
    body: JSON.stringify({ challenge_token: challengeToken, code }),
  });

// Instagrapi – ask Instagram to (re)send the SMS / email verification code
export const resendInstagrapiChallenge = (workspaceId: number, challengeToken: string) =>
  request<{
    sent: boolean;
    error?: string;
    auth_status?: string;
  }>(`/accounts/workspaces/${workspaceId}/resend_instagrapi_challenge/`, {
    method: 'POST',
    body: JSON.stringify({ challenge_token: challengeToken }),
  });

// Instagrapi – delete the stored session
export const deleteInstagrapiSession = (workspaceId: number) =>
  request<{ deleted: boolean }>(`/accounts/workspaces/${workspaceId}/delete_instagrapi_session/`, {
    method: 'POST',
    body: JSON.stringify({}),
  });

// Instagram Business Login (official Graph OAuth) – admin/beta only for now.
// Returns the authorization URL the browser should be redirected to.
export const getInstagramOAuthUrl = (workspaceId: number) =>
  request<{ url: string }>(`/accounts/workspaces/${workspaceId}/instagram_oauth_url/`);

// Remove the stored Instagram Graph OAuth token + IG user id.
export const disconnectInstagramGraph = (workspaceId: number) =>
  request<{ disconnected: boolean }>(
    `/accounts/workspaces/${workspaceId}/instagram_disconnect/`,
    { method: 'POST', body: JSON.stringify({}) }
  );

// Supervisor Agent
export const getSupervisorStatus = (workspaceId: number) =>
  request<SupervisorStatus>(`/agent/supervisor/status/?workspace_id=${workspaceId}`);

export const runSupervisorNow = (workspaceId: number) =>
  request<{ job_id: string; status: string; message: string }>(
    `/agent/supervisor/run/?workspace_id=${workspaceId}`,
    { method: 'POST', body: JSON.stringify({}) }
  );

export const getInteractionStatus = (workspaceId: number) =>
  request<InteractionStatus>(`/agent/interaction/status/?workspace_id=${workspaceId}`);

export const runInteractionNow = (workspaceId: number) =>
  request<{ job_id: string; status: string; message: string }>(
    `/agent/interaction/run/?workspace_id=${workspaceId}`,
    { method: 'POST', body: JSON.stringify({}) }
  );

export const getInteractionEvents = (
  workspaceId: number,
  params: { kind?: string; status?: string; limit?: number; offset?: number } = {}
) => {
  const q = new URLSearchParams({ workspace_id: String(workspaceId) });
  if (params.kind) q.set('kind', params.kind);
  if (params.status) q.set('status', params.status);
  if (params.limit != null) q.set('limit', String(params.limit));
  if (params.offset != null) q.set('offset', String(params.offset));
  return request<InteractionEventsResponse>(`/agent/interaction/events/?${q.toString()}`);
};

export const sendInteractionReply = (workspaceId: number, eventId: string, text?: string) =>
  request<InteractionEventLog>(
    `/agent/interaction/events/${eventId}/send/?workspace_id=${workspaceId}`,
    { method: 'POST', body: JSON.stringify(text != null ? { text } : {}) }
  );

export const dismissInteractionReply = (workspaceId: number, eventId: string) =>
  request<InteractionEventLog>(
    `/agent/interaction/events/${eventId}/dismiss/?workspace_id=${workspaceId}`,
    { method: 'POST', body: JSON.stringify({}) }
  );

// Human tasks — the "Needs You" inbox (work the AI hands over to the human)
export const getHumanTasks = (
  workspaceId: number,
  params: { status?: HumanTaskStatus; limit?: number; offset?: number } = {}
) => {
  const q = new URLSearchParams({ workspace_id: String(workspaceId) });
  if (params.status) q.set('status', params.status);
  if (params.limit != null) q.set('limit', String(params.limit));
  if (params.offset != null) q.set('offset', String(params.offset));
  return request<HumanTasksResponse>(`/agent/human-tasks/?${q.toString()}`);
};

export const updateHumanTaskStatus = (
  workspaceId: number,
  taskId: string,
  status: HumanTaskStatus
) =>
  request<HumanTask>(`/agent/human-tasks/${taskId}/?workspace_id=${workspaceId}`, {
    method: 'POST',
    body: JSON.stringify({ status }),
  });

// Onboarding flow
export const chatOnboardingCollector = (data: {
  history: Array<{ role: 'user' | 'assistant'; content: string }>;
  message: string;
}) =>
  request<{
    ready_for_account?: boolean;
    message: string;
    /** Tappable answers to the question just asked, generated from the niche. */
    quick_replies?: string[];
    /** Label for the button that leaves the chat — the agent's words, their language. */
    cta_label?: string;
    data?: {
      niche: string;
      tone?: string;
      goal?: string;
    };
  }>('/accounts/onboarding/chat/', {
    method: 'POST',
    body: JSON.stringify(data),
  });

/** One preview post — written before payment, rendered after it. */
export type OnboardingPreviewPost = {
  title: string;
  summary: string;
  caption: string;
  image_brief: string;
  content_format: 'image' | 'carousel';
};

export type OnboardingPreview = {
  token: string;
  strategy: {
    sections: Array<{ key: string; title: string; body: string }>;
    posting_cadence: string;
    image_orientation: 'portrait' | 'square';
    /** Land in Workspace.profile_display_name / profile_bio at handover. */
    account_name: string;
    bio: string;
  };
  posts: OnboardingPreviewPost[];
};

/**
 * Build the strategy for an account that does not exist yet.
 *
 * Returns a session token the caller must keep and hand to `startOnboarding`,
 * or everything generated here is orphaned. Passing an existing `token` replays
 * the stored result instead of paying for a second completion.
 */
export const previewOnboardingStrategy = (data: {
  niche: string;
  tone?: string;
  goal?: string;
  token?: string;
}) =>
  request<OnboardingPreview>('/accounts/onboarding/preview/strategy/', {
    method: 'POST',
    body: JSON.stringify(data),
  });

/** Write the first posts against the strategy the visitor just read. Text only. */
export const previewOnboardingPosts = (data: { token: string; count?: number }) =>
  request<OnboardingPreview>('/accounts/onboarding/preview/posts/', {
    method: 'POST',
    body: JSON.stringify(data),
  });

export const startOnboarding = (data: {
  email: string;
  password: string;
  first_name?: string;
  niche: string;
  tone?: string;
  goal?: string;
  instagram_handle?: string;
  plan_code: string;
  success_url: string;
  cancel_url: string;
  onboarding_token?: string;
}) =>
  request<{
    user: User;
    workspace: Workspace;
    checkout_url: string | null;
    checkout_session_id: string | null;
  }>('/accounts/onboarding/', {
    method: 'POST',
    body: JSON.stringify(data),
  });

export const getActivationStatus = (workspaceId: number) =>
  request<{
    has_onboarding_profile: boolean;
    niche: string;
    plan_code: string;
    initial_generation_triggered: boolean;
    initial_generation_job_id: string | null;
    activation_completed: boolean;
    activation_completed_at: string | null;
    subscription_status: string;
    subscription_plan: string;
  }>(`/accounts/activation-status/?workspace_id=${workspaceId}`);
